import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, Check, X, Castle, BookOpen } from 'lucide-react';

// [analyzePassword function unchanged - same as before]
const analyzePassword = (password) => {
  if (!password) return { score: 0, feedback: [], crackTime: 0 };
  let score = 0;
  const feedback = [];
  if (password.length >= 8) score += 1;
  else feedback.push({ type: 'warning', message: 'At least 8 characters' });
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password)) score += 0.5;
  if (/[A-Z]/.test(password)) score += 0.5;
  if (/[0-9]/.test(password)) score += 0.5;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  if (/123|abc|qwe|pass|admin|login/.test(password.toLowerCase())) {
    score = Math.max(0, score - 2);
    feedback.push({ type: 'error', message: 'Avoid common patterns' });
  }
  if (/(.)\1{2,}/.test(password)) {
    score = Math.max(0, score - 1);
    feedback.push({ type: 'warning', message: 'Avoid repeating characters' });
  }
  const finalScore = Math.min(4, Math.max(0, score));
  return { score: finalScore, feedback, crackTime: Math.pow(10, finalScore * 3) };
};

const PasswordStrengthSimulator = ({
  config = {
    title: "Create Your Password Fortress",
    requirements: [
      { id: 'length', label: '8+ characters', check: (pwd) => pwd.length >= 8 },
      { id: 'uppercase', label: 'Uppercase', check: (pwd) => /[A-Z]/.test(pwd) },
      { id: 'lowercase', label: 'Lowercase', check: (pwd) => /[a-z]/.test(pwd) },
      { id: 'number', label: 'Number', check: (pwd) => /[0-9]/.test(pwd) },
      { id: 'symbol', label: 'Symbol', check: (pwd) => /[^A-Za-z0-9]/.test(pwd) },
      { id: 'noCommon', label: 'No common words', check: (pwd) => !/123|abc|qwe|pass|admin|login/.test(pwd.toLowerCase()) }
    ],
    strengthLevels: ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong']
  },
  onPasswordSubmit = (password, strength) => console.log('Submitted:', { password, strength })
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [analysis, setAnalysis] = useState({ score: 0, feedback: [], crackTime: 0 });
  const [fortressElements, setFortressElements] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const result = analyzePassword(password);
    setAnalysis(result);

    const elements = [];
    if (password.length > 0) elements.push({ type: 'base', strength: 'stone' });

    config.requirements.forEach((req, i) => {
      if (req.check(password)) {
        const wallStrength = result.score >= 3 ? 'stone' : result.score >= 2 ? 'brick' : 'wood';
        elements.push({ type: 'wall', id: req.id, strength: wallStrength, position: i });
      }
    });

    if (result.score >= 3) elements.push({ type: 'tower', strength: 'stone', position: 'left' });
    if (result.score >= 4) {
      elements.push({ type: 'tower', strength: 'stone', position: 'right' });
      elements.push({ type: 'flag', strength: 'royal' });
    }
    if (result.score >= 2) elements.push({ type: 'gate', strength: result.score >= 3 ? 'reinforced' : 'basic' });

    setFortressElements(elements);
  }, [password, config.requirements]);

  const getStrengthColor = (score) => {
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
    return colors[score] || 'bg-gray-500';
  };

  const getStrengthLabel = (score) => config.strengthLevels[score] || 'Unknown';

  const FortressElement = ({ element }) => {
    const base = "transition-all duration-700 ease-out";

    switch (element.type) {
      case 'base':
        return <div className={`${base} absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-900 to-gray-700 rounded-b-xl shadow-2xl`}></div>;

      case 'wall':
        const wallColors = {
          wood: 'from-amber-700 to-amber-500 border-amber-800',
          brick: 'from-red-700 to-red-500 border-red-800',
          stone: 'from-gray-600 to-gray-400 border-gray-700'
        };
        return (
          <div
            className={`${base} absolute h-16 w-12 bg-gradient-to-t ${wallColors[element.strength]} border-2 rounded-t-xl shadow-xl`}
            style={{ left: `${22 + element.position * 13}%`, bottom: '32px', transform: 'translateX(-50%)' }}
          >
            <div className="h-3 bg-white/20 rounded-t-xl"></div>
          </div>
        );

      case 'tower':
        return (
          <div
            className={`${base} absolute h-20 w-16 bg-gradient-to-t from-gray-800 to-gray-500 border-2 border-gray-900 rounded-t-xl shadow-2xl`}
            style={{ [element.position]: '14%', bottom: '32px' }}
          >
            <div className="h-4 bg-white/30 rounded-t-xl"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-4 h-6 bg-red-700 rounded-sm shadow-md"></div>
          </div>
        );

      case 'gate':
        const gate = element.strength === 'reinforced' ? 'from-amber-900 to-amber-700' : 'from-amber-800 to-amber-600';
        return (
          <div
            className={`${base} absolute h-18 w-14 bg-gradient-to-t ${gate} border-2 border-amber-900 rounded-t-xl shadow-xl`}
            style={{ left: '50%', bottom: '32px', transform: 'translateX(-50%)' }}
          >
            <div className="absolute inset-x-2 top-2 bottom-4 bg-black/70 rounded-lg"></div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8 h-2 bg-amber-900 rounded-full"></div>
          </div>
        );

      case 'flag':
        return (
          <div className={`${base} absolute right-10 bottom-24 animate-pulse`}>
            <div className="w-1 h-14 bg-amber-900"></div>
            <div className="absolute top-0 left-1 w-8 h-5 bg-gradient-to-r from-purple-600 to-purple-800 shadow-lg">
              <div className="h-2 bg-gradient-to-r from-yellow-400 to-yellow-600"></div>
            </div>
          </div>
        );

      default: return null;
    }
  };

  const handleSubmit = () => {
    if (password && analysis.score >= 2) {
      onPasswordSubmit(password, { score: analysis.score, strength: getStrengthLabel(analysis.score), analysis });
    }
  };

  const handleLearnMore = () => {
    if (config?.learnMoreLink) {
      window.open(config.learnMoreLink, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/learning/module/password_security?topic=password_security`);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 flex items-center justify-center p-4 md:p-6 overflow-hidden">
      
      {/* Sun */}
      <div className="absolute top-8 right-10 w-16 h-16 bg-yellow-300 rounded-full shadow-xl animate-pulse">
        <div className="absolute inset-1.5 bg-yellow-200 rounded-full"></div>
      </div>

      {/* Main Container */}
      <div className="w-full max-w-7xl mx-auto grid md:grid-cols-12 gap-4 items-start">

        {/* === LEFT: Requirements Panel === */}
        <div className="md:col-span-3">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl p-5 shadow-xl border border-amber-200 h-fit">
            <h3 className="font-bold text-amber-800 text-sm flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5" /> Requirements
            </h3>
            <div className="space-y-2">
              {config.requirements.map(req => {
                const valid = req.check(password);
                return (
                  <div
                    key={req.id}
                    className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium transition-all ${
                      valid ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center border ${
                      valid ? 'border-green-500 bg-green-500/20' : 'border-gray-400'
                    }`}>
                      {valid ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3" />}
                    </div>
                    <span>{req.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* === CENTER: Input + Fortress === */}
        <div className="md:col-span-6 space-y-5">
          {/* Title */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2">
              <Castle className="w-8 h-8 text-amber-600" />
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                {config.title}
              </h1>
            </div>
          </div>

          {/* Input Card */}
          

          {/* Fortress Animation (Reduced Height) */}
          <div className="relative h-56 bg-gradient-to-t from-green-700 to-green-500 rounded-2xl overflow-hidden shadow-xl">
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-green-900 to-transparent"></div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-full max-w-md">
              {fortressElements.map((el, i) => (
                <FortressElement key={`${el.type}-${el.id || i}`} element={el} />
              ))}

              {/* Empty State */}
              {!password && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center bg-white/90 backdrop-blur p-4 rounded-xl shadow-lg">
                    <Castle className="w-10 h-10 mx-auto mb-2 text-amber-600" />
                    <p className="text-sm font-bold text-amber-800">Start typing!</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white/85 backdrop-blur-xl rounded-2xl p-5 shadow-2xl border border-amber-200">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-amber-300 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-amber-300 text-base font-medium pr-12 shadow-inner"
                placeholder="Enter password..."
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-700 hover:text-amber-900"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Strength Meter */}
            {password && (
              <div className="mt-3">
                <div className="flex justify-between text-xs font-bold text-amber-800 mb-1">
                  <span>Strength</span>
                  <span className={analysis.score >= 3 ? 'text-green-600' : analysis.score >= 2 ? 'text-amber-600' : 'text-red-600'}>
                    {getStrengthLabel(analysis.score)}
                  </span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getStrengthColor(analysis.score)} transition-all duration-700 rounded-full relative overflow-hidden`}
                    style={{ width: `${(analysis.score / 4) * 100}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shine"></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={!password || analysis.score < 2}
              className={`px-6 py-3 rounded-xl font-bold text-sm shadow-lg transition-all ${
                password && analysis.score >= 2
                  ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-400 hover:to-orange-500 hover:scale-105'
                  : 'bg-gray-400 text-gray-600 cursor-not-allowed'
              }`}
            >
              {password && analysis.score >= 2 ? 'Secure Fortress!' : 'Build Stronger'}
            </button>
          </div>
        </div>

        {/* === RIGHT: Suggestion Pop-ups === */}
        <div className="md:col-span-3">
          <div className="space-y-3">
            {analysis.feedback.map((f, i) => (
              <div
                key={i}
                className={`p-3 rounded-xl text-xs font-medium text-white shadow-lg animate-in slide-in-from-right duration-500 delay-${i * 100} ${
                  f.type === 'error' ? 'bg-red-600' : 'bg-amber-600'
                }`}
              >
                {f.message}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shine { animation: shine 2s infinite; }
      `}</style>
    </div>
  );
};

export default PasswordStrengthSimulator;